import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { portalService } from "apiServices";
import { IMG_PATH } from "utils/constants/imgPath";
import { RenderCommaMoney } from "utils/functions/FormatMoney";
import { Buttons, Displays, Inputs, Modals } from "components";
import { AlertControl } from "components/Modals/Alert";
import {
  BOOKING_SOURCE,
  BOOKING_STATUS,
  CREATED_BY,
  DOWNLOAD_BOOKING_TEMPLATE,
  RENDER_BOOKING_SOURCE,
  RENDER_SCHOOL,
  RENDER_SEAT_BOOKING_ERROR,
  RENDER_SEAT_BOOKING_STATUS,
  ADMIN_BOOKING_WARNINGS,
  SLIP_AMOUNT_CHECK,
} from "utils/functions/seatBooking";
import BookingDetailModal from "../../Modals/BookingDetailModal";
import ImportBookingModal from "../../Modals/ImportBookingModal";
import ReasonModal from "../../Modals/ReasonModal";

const STATUS_OPTIONS = [
  { value: "", label: "ทุกสถานะ" },
  { value: BOOKING_STATUS.PENDING, label: "รอตรวจสลิป" },
  { value: BOOKING_STATUS.APPROVED, label: "จองสำเร็จ" },
  { value: BOOKING_STATUS.REJECTED, label: "ปฏิเสธ" },
  { value: BOOKING_STATUS.CANCELLED_BY_ADMIN, label: "ยกเลิกโดยแอดมิน" },
];
const SOURCE_OPTIONS = [
  { value: "", label: "ทุกประเภทการจอง" },
  { value: BOOKING_SOURCE.PAID, label: "จ่ายเงิน" },
  { value: BOOKING_SOURCE.INTERNAL, label: "ภายใน/VIP (ไม่คิดเงิน)" },
];
const CREATED_BY_OPTIONS = [
  { value: "", label: "ทุกช่องทางการจอง" },
  { value: CREATED_BY.CUSTOMER, label: "ลูกค้าจองเอง" },
  { value: CREATED_BY.ADMIN, label: "แอดมินจองให้" },
];
const EMPTY_FILTER = { status: "", school_code: "", booking_source: "", created_by_type: "", keyword: "", has_warning: false };

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");
// หน้าต่างยืนยันอยู่นอกกรอบ styled ของหน้านี้ จึงใส่สไตล์ไว้ตรงนี้
const WarningList = ({ items }) => (
  <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "#fff4e0", color: "#d93333", textAlign: "left", fontSize: 14 }}>
    <div>กรุณาตรวจสอบก่อนอนุมัติ:</div>
    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
      {items.map((w) => (
        <li key={w}>{w}</li>
      ))}
    </ul>
  </div>
);

const BookingTab = ({ activityId, schools, canUpdate }) => {
  const [_isLoading, _setIsLoading] = useState(true);
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_filter, _setFilter] = useState(EMPTY_FILTER);
  const [_appliedFilter, _setAppliedFilter] = useState(EMPTY_FILTER);
  const [_data, _setData] = useState({ items: [], summary: null });
  const [_checked, _setChecked] = useState([]);
  const [_modal, _setModal] = useState(null);

  const _fetchData = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(_appliedFilter).forEach(([key, value]) => {
      if (value === true) params.append(key, "true");
      else if (value) params.append(key, value);
    });
    const query = params.toString();
    const res = await portalService.GET_SEAT_BOOKING_LIST(activityId, query ? `?${query}` : "");
    if (res && res.status === 200) {
      _setData(res.data);
      _setChecked([]);
    }
    _setIsLoading(false);
  }, [activityId, _appliedFilter]);

  useEffect(() => {
    _fetchData();
  }, [_fetchData]);

  const _callAction = async (request, successMessage) => {
    _setIsBgLoading(true);
    const res = await request();
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success(successMessage);
      _setModal(null);
      _fetchData();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res));
    }
  };

  const _approve = (booking) => {
    const warnings = ADMIN_BOOKING_WARNINGS(booking);
    AlertControl.show({
      title: "อนุมัติการจอง",
      description: (
        <>
          อนุมัติ {booking.code} ({booking.seat_ids.length} ที่นั่ง) และออกตั๋ว QR ใช่หรือไม่
          {warnings.length > 0 && <WarningList items={warnings} />}
        </>
      ),
      btnLabel1: "ยกเลิก",
      btnLabel2: "อนุมัติ",
      onEvent2: () => _callAction(() => portalService.POST_SEAT_BOOKING_APPROVE(booking._id), `อนุมัติ ${booking.code} แล้ว`),
    });
  };

  const _bulkApprove = () => {
    const flagged = _data.items
      .filter((b) => _checked.includes(String(b._id)))
      .map((b) => ({ code: b.code, warnings: ADMIN_BOOKING_WARNINGS(b) }))
      .filter((b) => b.warnings.length);
    AlertControl.show({
      title: "อนุมัติหลายรายการ",
      description: (
        <>
          อนุมัติ {_checked.length} รายการที่เลือกใช่หรือไม่
          {flagged.length > 0 && (
            <WarningList items={flagged.map((b) => `${b.code}: ${b.warnings.join(" / ")}`)} />
          )}
        </>
      ),
      btnLabel1: "ยกเลิก",
      btnLabel2: "อนุมัติ",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.POST_SEAT_BOOKING_BULK_APPROVE({ booking_ids: _checked });
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success(`อนุมัติแล้ว ${res.data.approved.length} รายการ`);
          if (res.data.failed.length) toast.error(`ไม่สำเร็จ ${res.data.failed.length} รายการ (สถานะเปลี่ยนแล้ว)`);
          _fetchData();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res));
        }
      },
    });
  };

  const _handleReason = (reason) => {
    const { type, booking } = _modal;
    if (type === "reject") {
      return _callAction(() => portalService.POST_SEAT_BOOKING_REJECT(booking._id, { reason }), `ปฏิเสธ ${booking.code} แล้ว ที่นั่งกลับเป็นว่าง`);
    }
    if (type === "cancel") {
      return _callAction(() => portalService.POST_SEAT_BOOKING_CANCEL(booking._id, { reason }), `ยกเลิก ${booking.code} แล้ว ที่นั่งกลับเป็นว่าง`);
    }
    return _callAction(() => portalService.POST_SEAT_BOOKING_UNDO(booking._id, { reason }), `ย้อนกลับ ${booking.code} เป็นรอตรวจสลิปแล้ว`);
  };

  // Export หนึ่งแถวต่อที่นั่ง (ใช้เป็นรายชื่อผู้ชม/ผังหน้างาน) ตามตัวกรองปัจจุบัน
  const _handleExport = () => {
    const header = [
      "ที่นั่ง", "ชื่อผู้ชม", "รหัสจอง", "สถานะ", "ประเภท", "โรงเรียน", "ผู้จอง", "เบอร์โทร", "อีเมล",
      "ราคาต่อที่นั่ง (บาท)", "ใบกำกับภาษี", "ชื่อ/บริษัท", "สาขา", "เลขผู้เสียภาษี", "ที่อยู่ใบกำกับ",
      "ช่องทาง", "แอดมินที่จอง", "ส่งเมื่อ", "เข้างาน", "หมายเหตุ", "คำเตือน",
    ];
    const rows = _data.items.flatMap((b) =>
      b.seats.map((s) => [
        s.seat_id,
        s.viewer_name || "",
        b.code,
        RENDER_SEAT_BOOKING_STATUS(b.status).labelTh,
        RENDER_BOOKING_SOURCE(b.booking_source),
        RENDER_SCHOOL(b.school_code, b.school_name),
        `${(b.booker && b.booker.first_name) || ""} ${(b.booker && b.booker.last_name) || ""}`.trim(),
        (b.booker && b.booker.phone) || "",
        (b.booker && b.booker.email) || "",
        b.booking_source === BOOKING_SOURCE.INTERNAL ? 0 : b.price_per_seat,
        b.tax_invoice && b.tax_invoice.is_required ? "ต้องการ" : "",
        (b.tax_invoice && b.tax_invoice.name) || "",
        (b.tax_invoice && b.tax_invoice.branch) || "",
        (b.tax_invoice && b.tax_invoice.tax_id) || "",
        (b.tax_invoice && b.tax_invoice.address) || "",
        b.created_by_type === CREATED_BY.ADMIN ? "แอดมินจองให้" : "ลูกค้าจองเอง",
        b.created_by_admin_name || "",
        formatDate(b.submitted_at),
        s.used_at ? formatDate(s.used_at) : "",
        b.note || "",
        (b.warnings || []).join(" / "),
      ])
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...rows]), "Seats");
    XLSX.writeFile(wb, `SeatBooking_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  const pendingIds = _data.items.filter((b) => b.status === BOOKING_STATUS.PENDING).map((b) => String(b._id));
  const summary = _data.summary;

  if (_isLoading) return <Displays.Loading theme_standard_loading heightLayout="360px" />;

  return (
    <div>
      <Displays.BGLoading visible={_isBgLoading} />
      {summary && (
        <>
          <div className="stat_row">
            <div className="stat_item">
              <div className="stat_label">รอตรวจสลิป</div>
              <div className="stat_value">{summary.pending_count}</div>
            </div>
            <div className="stat_item">
              <div className="stat_label">จองสำเร็จ (ที่นั่ง)</div>
              <div className="stat_value">{summary.approved_seats}</div>
            </div>
            <div className="stat_item">
              <div className="stat_label">ยอดเงินยืนยันแล้ว</div>
              <div className="stat_value">{summary.confirmed_amount ? RenderCommaMoney(summary.confirmed_amount) : 0}</div>
            </div>
            <div className="stat_item">
              <div className="stat_label">ภายใน/VIP (ที่นั่ง)</div>
              <div className="stat_value">{summary.internal_seats}</div>
            </div>
            <div className="stat_item">
              <div className="stat_label">เข้างานแล้ว</div>
              <div className="stat_value">
                {summary.entered_seats} / {summary.approved_seats}
              </div>
            </div>
            <div className="stat_item">
              <div className="stat_label">โควตาออนไลน์ใช้ไป</div>
              <div className="stat_value">
                {summary.online_used}
                {summary.online_quota ? ` / ${summary.online_quota}` : ""}
              </div>
            </div>
          </div>
          {/* ภายใน/VIP (GUEST) ไม่ใช่โรงเรียน และมีการ์ดของตัวเองด้านบนแล้ว */}
          {summary.by_school.some((s) => s.school_code !== "GUEST") && (
            <div className="chip_row">
              <span>ยอดตามโรงเรียน:</span>
              {summary.by_school.filter((s) => s.school_code !== "GUEST").map((s) => (
                <span className="chip static" key={s.school_code}>
                  {s.school_code === "GEN" ? "ไม่ระบุโรงเรียน" : s.school_code} · {s.approved_seats} ที่ · {s.approved_amount ? RenderCommaMoney(s.approved_amount) : 0}
                  {s.pending_count ? ` (รอ ${s.pending_count})` : ""}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      <div className="filter_row">
        <div className="filter_item">
          <Inputs.Dropdown
            value={_filter.status}
            placeholder={STATUS_OPTIONS[0].label}
            options={STATUS_OPTIONS}
            onChange={(v) => _setFilter((f) => ({ ...f, status: v }))}
          />
        </div>
        <div className="filter_item">
          <Inputs.Dropdown
            value={_filter.school_code}
            placeholder="ทุกโรงเรียน"
            options={[
              { value: "", label: "ทุกโรงเรียน" },
              { value: "GEN", label: "ไม่ระบุโรงเรียน" },
              ...schools.map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` })),
            ]}
            onChange={(v) => _setFilter((f) => ({ ...f, school_code: v }))}
          />
        </div>
        <div className="filter_item">
          <Inputs.Dropdown
            value={_filter.booking_source}
            placeholder={SOURCE_OPTIONS[0].label}
            options={SOURCE_OPTIONS}
            onChange={(v) => _setFilter((f) => ({ ...f, booking_source: v }))}
          />
        </div>
        <div className="filter_item">
          <Inputs.Dropdown
            value={_filter.created_by_type}
            placeholder={CREATED_BY_OPTIONS[0].label}
            options={CREATED_BY_OPTIONS}
            onChange={(v) => _setFilter((f) => ({ ...f, created_by_type: v }))}
          />
        </div>
        <div className="filter_keyword">
          <Inputs.TextField
            value={_filter.keyword}
            placeholder="ค้นหา รหัสจอง / ชื่อ / เบอร์ / ที่นั่ง"
            onChange={(e) => _setFilter((f) => ({ ...f, keyword: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && _setAppliedFilter(_filter)}
          />
        </div>
        <Inputs.InputCheckBox
          theme_standard_input_check_box
          label="เฉพาะที่มีคำเตือน"
          checked={_filter.has_warning}
          onChange={() => _setFilter((f) => ({ ...f, has_warning: !f.has_warning }))}
        />
        <div className="w100">
          <Buttons.BgStandard label="ค้นหา" onClick={() => _setAppliedFilter(_filter)} />
        </div>
      </div>

      <div className="inline_row" style={{ justifyContent: "flex-end" }}>
        {canUpdate && (
          <div className="w200">
            <Buttons.BgStandard
              theme_green
              label={`อนุมัติที่เลือก (${_checked.length})`}
              disabled={!_checked.length}
              onClick={_bulkApprove}
            />
          </div>
        )}
        {canUpdate && (
          <div className="w160">
            <Buttons.BgStandard label="นำเข้า Excel" onClick={() => _setModal({ type: "import" })} />
          </div>
        )}
        <div className="w200">
          <Buttons.BgStandard theme_export_btn label="Download Template" onClick={DOWNLOAD_BOOKING_TEMPLATE} />
        </div>
        <div className="w120">
          <Buttons.BgStandard theme_export_btn label="Export" onClick={_handleExport} />
        </div>
      </div>

      <div className="table_wrap">
        <table className="seat_table">
          <thead>
            <tr>
              <th>
                {canUpdate && pendingIds.length > 0 && (
                  <Inputs.InputCheckBox
                    theme_standard_input_check_box
                    checked={pendingIds.length > 0 && pendingIds.every((id) => _checked.includes(id))}
                    onChange={(e) => _setChecked(e.target.checked ? pendingIds : [])}
                  />
                )}
              </th>
              <th>ส่งเมื่อ</th>
              <th>รหัสจอง</th>
              <th>ผู้จอง</th>
              <th>โรงเรียน</th>
              <th>ที่นั่ง / ชื่อผู้ชม</th>
              <th>ยอด</th>
              <th>สลิป</th>
              <th>สถานะ</th>
              <th>ช่องทาง</th>
              <th>เข้างาน</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {_data.items.length === 0 && (
              <tr>
                <td colSpan={12} className="muted">
                  ไม่มีรายการ
                </td>
              </tr>
            )}
            {_data.items.map((b) => {
              const id = String(b._id);
              const status = RENDER_SEAT_BOOKING_STATUS(b.status);
              const isPending = b.status === BOOKING_STATUS.PENDING;
              const isApproved = b.status === BOOKING_STATUS.APPROVED;
              const isRejected = b.status === BOOKING_STATUS.REJECTED;
              const isCustomer = b.created_by_type === CREATED_BY.CUSTOMER;
              return (
                <tr key={id}>
                  <td>
                    {canUpdate && isPending && (
                      <Inputs.InputCheckBox
                        theme_standard_input_check_box
                        checked={_checked.includes(id)}
                        onChange={(e) =>
                          _setChecked((prev) => (e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)))
                        }
                      />
                    )}
                  </td>
                  <td>{formatDate(b.submitted_at)}</td>
                  <td className="nowrap">
                    <button type="button" className="link_btn" onClick={() => _setModal({ type: "detail", booking: b })}>
                      {b.code}
                    </button>
                  </td>
                  <td>
                    {b.booker && `${b.booker.first_name || ""} ${b.booker.last_name || ""}`}
                    <div className="muted">{b.booker && b.booker.phone}</div>
                  </td>
                  <td>{RENDER_SCHOOL(b.school_code)}</td>
                  <td>
                    {b.seats.map((s) => (
                      <div key={s.seat_id}>
                        {s.seat_id}
                        {s.viewer_name ? <span className="muted"> · {s.viewer_name}</span> : null}
                      </div>
                    ))}
                  </td>
                  <td>
                    {b.booking_source === BOOKING_SOURCE.INTERNAL ? "-" : RenderCommaMoney(b.amount)}
                    {SLIP_AMOUNT_CHECK(b).status === "mismatch" && (
                      <div className="amount_mismatch">โอนจริง {RenderCommaMoney(SLIP_AMOUNT_CHECK(b).paid)}</div>
                    )}
                  </td>
                  <td>
                    {(b.payments || []).length === 0
                      ? <span className="muted">{b.booking_source === BOOKING_SOURCE.INTERNAL ? "-" : "ไม่มีสลิป"}</span>
                      : b.payments.map((p, i) => (
                          <div key={p._id || i}>
                            {/* แบบเดียวกับตารางสลิปของ Music Hall */}
                            <a href={`${IMG_PATH}${p.slip_path}`} target="_blank" rel="noopener noreferrer">
                              Link{b.payments.length > 1 ? ` ${i + 1}` : ""}
                            </a>
                          </div>
                        ))}
                  </td>
                  <td className="status_cell">
                    <Displays.StatusLabel bg={status.bgColor} txtColor={status.txtColor} label={status.labelTh} />
                    {ADMIN_BOOKING_WARNINGS(b).map((w) => (
                      <span className="warn_badge" key={w}>
                        {w}
                      </span>
                    ))}
                    {b.reject_reason && <div className="muted">เหตุผล: {b.reject_reason}</div>}
                    {b.cancel_reason && <div className="muted">เหตุผล: {b.cancel_reason}</div>}
                  </td>
                  <td>
                    {RENDER_BOOKING_SOURCE(b.booking_source)}
                    <div className="muted">
                      {isCustomer ? "ลูกค้าจองเอง" : `แอดมิน: ${b.created_by_admin_name || "-"}`}
                    </div>
                    {b.decided_by_admin_name && isCustomer && (
                      <div className="muted">ตัดสินโดย {b.decided_by_admin_name}</div>
                    )}
                  </td>
                  <td>{isApproved ? `${b.used_count} / ${b.seats.length}` : "-"}</td>
                  <td>
                    <div className="row_btns">
                      {canUpdate && isPending && (
                        <>
                          <div className="w100">
                            <Buttons.BgStandard theme_green label="อนุมัติ" onClick={() => _approve(b)} />
                          </div>
                          <div className="w100">
                            <Buttons.BgStandard theme_red label="ปฏิเสธ" onClick={() => _setModal({ type: "reject", booking: b })} />
                          </div>
                        </>
                      )}
                      {canUpdate && isCustomer && ((isApproved && !b.used_count) || isRejected) && (
                        <div className="w100">
                          <Buttons.BgStandard theme_gray label="ย้อนกลับ" onClick={() => _setModal({ type: "undo", booking: b })} />
                        </div>
                      )}
                      {canUpdate && (isPending || isApproved) && !b.used_count && (
                        <div className="w100">
                          <Buttons.BgStandard theme_red label="ยกเลิก" onClick={() => _setModal({ type: "cancel", booking: b })} />
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modals.BodyEmpty isShowModal={!!_modal} onCloseModal={() => _setModal(null)}>
        {_modal && _modal.type === "detail" && (
          <BookingDetailModal bookingId={_modal.booking._id} onClose={() => _setModal(null)} />
        )}
        {_modal && _modal.type === "import" && (
          <ImportBookingModal
            activityId={activityId}
            onClose={() => _setModal(null)}
            onSuccess={() => {
              _setModal(null);
              _fetchData();
            }}
          />
        )}
        {_modal && ["reject", "cancel", "undo"].includes(_modal.type) && (
          <ReasonModal
            title={
              _modal.type === "reject"
                ? `ปฏิเสธ ${_modal.booking.code} (ที่นั่งจะกลับเป็นว่าง)`
                : _modal.type === "cancel"
                  ? `ยกเลิก ${_modal.booking.code} (ที่นั่งจะกลับเป็นว่าง คืนเงินกันนอกระบบ)`
                  : `ย้อนกลับ ${_modal.booking.code} เป็นรอตรวจสลิป${_modal.booking.status === BOOKING_STATUS.APPROVED ? " (ตั๋วเดิมจะใช้ไม่ได้)" : ""}`
            }
            label={_modal.type === "undo" ? "หมายเหตุ (ไม่บังคับ)" : "เหตุผล * (ลูกค้าจะเห็นเหตุผลนี้)"}
            required={_modal.type !== "undo"}
            confirmLabel={_modal.type === "reject" ? "ปฏิเสธ" : _modal.type === "cancel" ? "ยกเลิกการจอง" : "ย้อนกลับ"}
            onClose={() => _setModal(null)}
            onConfirm={_handleReason}
          />
        )}
      </Modals.BodyEmpty>
    </div>
  );
};

export default BookingTab;
